// ADAPT - the one continuous slider of a "universal" visualizer.
//
// A universal visualizer has no modes: it sits on a CURSOR between a calm
// register and an intense one, driven by the music. A ballad parks it near
// the calm end, techno near the intense end, a track that builds slides it.
// Nothing here assumes a drop, a clean beat grid or a steady tempo.
//
//   const adapt = new Adapt();             // one per visualizer instance
//   adapt.update(dt, profile);             // once per frame
//   const a = adapt.get();
//
// a.intensity  0..1  segment energy smoothed over 4 bars (8 s without a
//                    tempo), mixed with percussiveness - the main cursor
// a.motion     0.25..1  general speed of every movement
// a.pulse      0..1  beat-impulse amplitude = intensity x percussiveness x
//                    gridStrength. Without a clean grid (classical, ambient,
//                    free jazz) it goes to 0: the visual BREATHES, it does
//                    not beat
// a.attack     0..1  ONE FRAME: the strength of the accent detected this frame
//                    (0 otherwise) - for visuals triggered by strong accents
// a.onset      0..1  an envelope fired by attacks in bands.low / bands.mid
//                    (a sharp rise over their running mean): the "beat" of
//                    music that has no grid. Rises in ~40 ms, half-life 0.2 s
// a.cutPolicy  'bar' | 'phrase' | 'fade'   (hysteresis, never flaps)
// a.colorMode  'vivid' | 'deep'            (hysteresis)
// a.saturation 0.65..0.9  the colour mode as a smoothed number - use it
//                    rather than switching on colorMode, nothing jumps
// a.lumaCap    0.25..0.45  R10 mean-luminance ceiling for this intensity
// a.dropReady  true only when the track HAS drops: drop effects are never
//                    simulated on a track without one
// a.beat, a.bar, a.phrase  one-frame events: from the grid when it is
//                    reliable, otherwise beat = an onset, bar = 4 onsets,
//                    phrase = 30 s
// a.beatPhase  0..1  phase inside the current beat (grid, or the mean onset
//                    interval without one)
// a.section    one-frame event: the structure changed section (analysis
//                    segment, or every 30 s phrase without one)
// a.drop       one-frame event: a drop mark is reached (only if dropReady)
// a.bands      {low, mid, high} 0..1 or null: the bands adapt itself read -
//                    the simulated ones under ?adaptsim (added for contours.js)
//
// Inputs, all optional with a fallback: the segment's energy
// (analysisTimeline.lookupEnergy, else trackFeatures.energy, else the
// director's synthetic envelope), director.character.{percussiveness,
// gridStrength, dynamics}, bpm, director.bands, the drop marks, structure.
//
// Debug: ?adaptsim=ballad|pop|rock|techno|classical|jazz replaces every
// input by a simulated profile (its own clock, bands, sections, drops) so
// each visualizer can be checked in each register; ?adaptshow=1 overlays
// the outputs.

function urlParam(name) {
  try { return new URLSearchParams(window.location.search).get(name); } catch (e) { return null; }
}
const clamp01 = (x) => Math.max(0, Math.min(1, x));
// exponential approach, frame-rate independent
const ease = (cur, target, dt, tau) => cur + (target - cur) * (1 - Math.exp(-dt / Math.max(tau, 1e-3)));

// --- the simulated profiles ---------------------------------------------------
// energy/perc/grid/dyn are the static character; swing (jazz) delays every
// second eighth; drops: every second section opens on a drop.
export const ADAPT_SIMS = {
  ballad:    { energy: 0.30, perc: 0.20, grid: 0.55, dyn: 0.65, bpm: 72, drops: false, kick: 0.25, snare: 0.15 },
  pop:       { energy: 0.60, perc: 0.55, grid: 0.85, dyn: 0.35, bpm: 112, drops: false, kick: 0.60, snare: 0.50 },
  rock:      { energy: 0.75, perc: 0.70, grid: 0.70, dyn: 0.45, bpm: 132, drops: false, kick: 0.70, snare: 0.75 },
  techno:    { energy: 0.92, perc: 0.95, grid: 1.00, dyn: 0.12, bpm: 128, drops: true, kick: 1.00, snare: 0.30 },
  classical: { energy: 0.28, perc: 0.08, grid: 0.10, dyn: 0.90, bpm: null, drops: false, kick: 0, snare: 0 },
  jazz:      { energy: 0.45, perc: 0.45, grid: 0.35, dyn: 0.60, bpm: 118, drops: false, kick: 0.30, snare: 0.35, swing: true }
};
const SIM_SECTION_S = 30;   // simulated sections (and the phrase fallback)

class AdaptSim {
  constructor(name) { this.name = name; this.p = ADAPT_SIMS[name]; this.t = 0; this._seed = 1; }
  _rnd() { this._seed = (this._seed * 16807) % 2147483647; return this._seed / 2147483647; }
  // a profile-shaped snapshot of the simulated music at this instant
  step(dt) {
    const p = this.p; this.t += dt; const t = this.t;
    const sec = Math.floor(t / SIM_SECTION_S);
    // energy: the character's level, a section lift (every other section
    // louder), a slow swell - so the cursor really slides
    const lift = (sec % 2 ? 0.10 : -0.08) + 0.06 * Math.sin(t * 2 * Math.PI / 47);
    const energy = clamp01(p.energy + lift);
    let low = 0.15 * p.energy, mid = 0.2 * p.energy, high = 0.15 * p.energy;
    let beatPhase = null, barPhase = null, phrasePhase = null;
    if (p.bpm) {
      const beats = t * p.bpm / 60;
      beatPhase = beats % 1; barPhase = (beats / 4) % 1; phrasePhase = (beats / 16) % 1;
      const bIn = Math.floor(beats) % 4;
      low += p.kick * Math.exp(-beatPhase * 9);                                     // kick on every beat
      if (bIn % 2 === 1) mid += p.snare * Math.exp(-beatPhase * 12);              // backbeat on 2 and 4
      const eighth = p.swing ? ((beatPhase < 0.66 ? beatPhase / 0.66 : (beatPhase - 0.66) / 0.34) % 1) : (beatPhase * 2) % 1;
      high += 0.3 * p.perc * Math.exp(-eighth * 10);
    } else {
      // no grid: swells, and attacks at irregular moments (2-5 s)
      if (this._next == null || t >= this._next) { this._last = t; this._next = t + 2 + 3 * this._rnd(); this._amp = 0.4 + 0.5 * this._rnd(); }
      const since = t - this._last;
      mid += 0.25 + 0.2 * Math.sin(t * 0.7) + this._amp * Math.exp(-since * 3) * (since < 0.04 ? since / 0.04 : 1);
      low += 0.15 + 0.1 * Math.sin(t * 0.43);
    }
    const isDrop = p.drops && sec % 2 === 1;
    return {
      energy, perc: p.perc, grid: p.grid, dyn: p.dyn, bpm: p.bpm,
      bands: { low: clamp01(low), mid: clamp01(mid), high: clamp01(high) },
      beatPhase, barPhase, phrasePhase, hasGrid: !!p.bpm,
      sectionIndex: sec, dropReady: p.drops,
      // the drop: the first instant of every odd section
      dropNow: isDrop && t - sec * SIM_SECTION_S < dt * 1.01
    };
  }
}

// --- reading a real profile ----------------------------------------------------
function readProfile(profile) {
  const d = profile.director || null;
  const tl = profile.analysisTimeline || null;
  const posMs = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
  const tf = profile.trackFeatures || null;
  let energy = null;
  if (tl && posMs != null && tl.lookupEnergy) { const e = tl.lookupEnergy(posMs); if (e != null) energy = e / 100; }
  if (energy == null && tf && tf.energy != null) energy = tf.energy / 100;
  if (energy == null && d && d.intensity != null) energy = d.intensity;
  const ch = (d && d.character) || {};
  // gridStrength: a measured grid counts fully (scaled by its confidence);
  // a tempo with an extrapolated grid half; nothing, nothing
  const bpm = (tf && tf.bpm) || null;
  const hasGrid = profile.beatPhase != null;
  let grid = ch.gridStrength != null ? ch.gridStrength
    : (d && d.hasReliableGrid) ? (profile.beatConfidence != null ? clamp01(0.4 + 0.6 * profile.beatConfidence) : 1)
    : (hasGrid && bpm) ? 0.5 : 0;
  // the section: the analysis segment we are in
  let sectionIndex = null;
  if (tl && posMs != null && tl.segments && tl.segments.length) {
    const s = tl.segments; let i = 0;
    while (i + 1 < s.length && s[i + 1].start_ms <= posMs) i++;
    sectionIndex = i;
  }
  const dropReady = !!(tl && tl.marks && tl.marks.some((m) => m.type === 'drop'));
  return {
    energy, perc: ch.percussiveness != null ? ch.percussiveness : null, grid,
    dyn: ch.dynamics != null ? ch.dynamics : null, bpm,
    bands: (d && d.bands) || null,
    beatPhase: profile.beatPhase, barPhase: profile.barPhase, phrasePhase: profile.phrasePhase, hasGrid,
    sectionIndex, dropReady,
    nextEvent: d ? d.nextEvent : null, trackId: profile.trackId || profile.title || null
  };
}

// --- the overlay ----------------------------------------------------------------
let overlayEl = null;
function overlay(text) {
  if (typeof document === 'undefined') return;
  if (!overlayEl) {
    overlayEl = document.createElement('pre');
    overlayEl.style.cssText = 'position:fixed;left:12px;bottom:12px;z-index:99999;margin:0;padding:8px 10px;' +
      'background:rgba(0,0,0,.65);color:#9f9;font:12px/1.35 ui-monospace,Menlo,monospace;pointer-events:none;white-space:pre';
    document.body.appendChild(overlayEl);
  }
  overlayEl.textContent = text;
}

export class Adapt {
  constructor(opts = {}) {
    const sim = opts.sim !== undefined ? opts.sim : urlParam('adaptsim');
    this._sim = sim && ADAPT_SIMS[sim] ? new AdaptSim(sim) : null;
    this._show = opts.show !== undefined ? opts.show : urlParam('adaptshow') === '1';
    // the smoothed state - starts calm and slides in
    this._energy = 0.35; this._perc = 0.3; this._grid = 0.3;
    this._intensity = 0.3; this._sat = 0.65;
    this._onset = 0; this._onsetTarget = 0;
    this._meanLow = 0; this._meanMid = 0; this._lastOnset = -9; this._onsetGap = 0.6;
    this._onsetCount = 0; this._t = 0; this._lastPhraseT = 0;
    this._cut = 'fade'; this._color = 'deep';
    this._prevBeat = null; this._prevBar = null; this._prevPhrase = null;
    this._section = null; this._nextDropMs = null;
    this._out = { intensity: 0.3, motion: 0.25, pulse: 0, onset: 0, cutPolicy: 'fade', colorMode: 'deep',
      saturation: 0.65, lumaCap: 0.25, dropReady: false, beat: false, bar: false, phrase: false,
      beatPhase: 0, section: false, drop: false, attack: 0, bands: null, sim: this._sim ? this._sim.name : null };
    this._showT = 0;
  }

  get simulated() { return this._sim ? this._sim.name : null; }

  update(dt, profile) {
    dt = Math.min(Math.max(dt || 0, 0), 0.1);
    this._t += dt;
    const o = this._out;
    o.beat = o.bar = o.phrase = o.section = o.drop = false; o.attack = 0;
    if (dt === 0) return o;              // paused: freeze, never reset
    const s = this._sim ? this._sim.step(dt) : readProfile(profile || {});

    // the static character, smoothed so a track change glides (2 s)
    const perc = s.perc != null ? s.perc : 0.4, grid = s.grid != null ? s.grid : 0;
    this._perc = ease(this._perc, perc, dt, 2);
    this._grid = ease(this._grid, grid, dt, 2);

    // INTENSITY: energy smoothed over 4 bars (8 s without a tempo), mixed with
    // percussiveness
    const window = s.bpm ? 16 * 60 / s.bpm : 8;
    this._energy = ease(this._energy, s.energy != null ? s.energy : 0.4, dt, window / 2);
    const target = clamp01(0.72 * this._energy + 0.28 * this._perc);
    this._intensity = ease(this._intensity, target, dt, 1.0);
    const I = this._intensity;

    // ONSETS: a sharp rise of low or mid over its running mean (~1.5 s)
    const b = s.bands;
    let attack = 0;
    if (b) {
      this._meanLow = ease(this._meanLow, b.low, dt, 1.5);
      this._meanMid = ease(this._meanMid, b.mid, dt, 1.5);
      const rl = b.low - (this._meanLow * 1.35 + 0.06), rm = b.mid - (this._meanMid * 1.35 + 0.06);
      const r = Math.max(rl, rm);
      if (r > 0 && this._t - this._lastOnset > 0.18 && !this._above) {
        attack = clamp01(0.5 + 2 * r);
        const gap = this._t - this._lastOnset;
        if (gap < 3) this._onsetGap = ease(this._onsetGap, gap, 1, 2);   // the mean interval, slowly
        this._lastOnset = this._t;
      }
      this._above = r > 0;
    }
    if (attack > 0) this._onsetTarget = Math.max(this._onsetTarget, attack);
    // rise in ~40 ms toward the attack, then decay (half-life 0.2 s): no jump
    this._onsetTarget *= Math.pow(0.5, dt / 0.2);
    this._onset = this._onsetTarget > this._onset ? ease(this._onset, this._onsetTarget, dt, 0.04) : this._onsetTarget;

    // EVENTS: from the grid when it is reliable, from the onsets otherwise
    const gridOk = s.hasGrid && this._grid > 0.4;
    const wrap = (cur, prev) => cur != null && prev != null && cur < prev - 0.5;
    if (gridOk) {
      o.beat = wrap(s.beatPhase, this._prevBeat);
      o.bar = wrap(s.barPhase, this._prevBar);
      o.phrase = wrap(s.phrasePhase, this._prevPhrase);
      o.beatPhase = s.beatPhase;
      if (o.phrase) this._lastPhraseT = this._t;
    } else {
      o.beat = attack > 0;
      if (o.beat) { this._onsetCount++; o.bar = this._onsetCount % 4 === 0; }
      o.phrase = this._t - this._lastPhraseT >= SIM_SECTION_S;
      if (o.phrase) this._lastPhraseT = this._t;
      o.beatPhase = clamp01((this._t - this._lastOnset) / Math.max(this._onsetGap, 0.25));
    }
    this._prevBeat = s.beatPhase; this._prevBar = s.barPhase; this._prevPhrase = s.phrasePhase;

    // SECTION: the analysis segment changed; without structure, every phrase
    if (s.sectionIndex != null) {
      if (this._section != null && s.sectionIndex !== this._section) o.section = true;
      this._section = s.sectionIndex;
    } else if (o.phrase) {
      // no structure: a section every 2 phrases on a grid (32 beats), every
      // 30 s phrase without one
      this._phraseCount = (this._phraseCount || 0) + 1;
      o.section = !gridOk || this._phraseCount % 2 === 0;
    }

    // DROP: only on a track that has drops
    o.dropReady = !!s.dropReady;
    if (o.dropReady) {
      if (this._sim) o.drop = !!s.dropNow;
      else if (s.nextEvent && s.nextEvent.type === 'drop') {
        // the event we were counting toward has just been passed
        if (this._nextDropMs != null && this._nextDropMs > 0 && s.nextEvent.msUntil > this._nextDropMs + 200) o.drop = true;
        this._nextDropMs = s.nextEvent.msUntil;
      } else {
        if (this._nextDropMs != null && this._nextDropMs < 250) o.drop = true;
        this._nextDropMs = null;
      }
    }

    // CUT POLICY and COLOUR MODE, with hysteresis (0.05) so they never flap
    const G = this._grid, h = 0.05;
    const barOk = this._cut === 'bar' ? (G > 0.6 - h && I > 0.6 - h) : (G > 0.6 + h && I > 0.6 + h);
    const phraseOk = this._cut !== 'fade' ? G > 0.4 - h : G > 0.4 + h;
    this._cut = barOk ? 'bar' : phraseOk ? 'phrase' : 'fade';
    this._color = this._color === 'vivid' ? (I > 0.5 - h ? 'vivid' : 'deep') : (I > 0.5 + h ? 'vivid' : 'deep');
    this._sat = ease(this._sat, this._color === 'vivid' ? 0.9 : 0.65, dt, 1.5);

    o.bands = s.bands || null;
    o.intensity = I;
    o.motion = 0.25 + 0.75 * I;
    o.pulse = clamp01(I * this._perc * G);
    o.onset = this._onset;
    o.attack = attack;                 // one frame: the strength (0..1) of an accent detected now, else 0
    o.cutPolicy = this._cut;
    o.colorMode = this._color;
    o.saturation = this._sat;
    o.lumaCap = 0.25 + 0.20 * I;
    if (this._show) {
      this._showT += dt;
      if (this._showT > 0.1) {
        this._showT = 0;
        const f = (x) => x.toFixed(2);
        overlay(`adapt${this._sim ? ' sim=' + this._sim.name : ''}\nintensity ${f(I)}  motion ${f(o.motion)}\npulse ${f(o.pulse)}  onset ${f(o.onset)}\n` +
          `cut ${o.cutPolicy}  colour ${o.colorMode} (${f(o.saturation)})\nlumaCap ${f(o.lumaCap)}  dropReady ${o.dropReady}\n` +
          `perc ${f(this._perc)}  grid ${f(G)}  energy ${f(this._energy)}\nbeatPhase ${f(o.beatPhase)}`);
      }
    }
    return o;
  }

  get() { return this._out; }

  dispose() {
    if (overlayEl && overlayEl.parentNode) overlayEl.parentNode.removeChild(overlayEl);
    overlayEl = null;
  }
}
